import React, { useCallback, useEffect, useMemo, useState } from 'react';
import posthog from 'posthog-js';
import QRCode from 'qrcode';
import {
  Button,
  Checkbox,
  Drawer,
  Input,
  List,
  Modal,
  Radio,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import { tripShareEn as t } from '../../content/tripShare.en';
import {
  addMember,
  listMembers,
  patchMemberRole,
  revokeMember,
  type ShareRole,
  type TripMembership,
  TripShareApiError,
} from '../../services/tripShareService';
import { listConnections } from '../../services/connectionsService';
import {
  connectionInvitePrimaryLabel,
  listPendingSentInvitesForTrip,
  type ConnectionInvite,
} from '../../services/inviteService';
import {
  createQrCodeToken,
  QrCodeApiError,
  qrInvitationUrl,
} from '../../services/qrCodeService';
import {
  displayNameForSub,
  resolveDisplayNames,
} from '../../services/tripShareDisplayNames';
import {
  loadLocalUserPreferences,
  patchAppUserSettings,
} from '../../services/userPreferencesSyncService';
import ConnectionInviteForm from '../ConnectionInviteForm/ConnectionInviteForm';
import { captureSanitizedException, isPostHogConfigured } from '../../lib/posthog';

type ConnectionRow = {
  connectedUserId: string;
  displayName?: string | null;
  username?: string | null;
};

type InviteChannel = 'qr' | 'connection' | 'email' | 'sms';
type DrawerView = 'roster' | 'invite';

type Props = {
  open: boolean;
  tripId: string;
  tripName?: string | null;
  tripEndDate?: string | null;
  onClose: () => void;
};

const TripShareRoster: React.FC<Props> = ({
  open,
  tripId,
  tripName,
  tripEndDate,
  onClose,
}) => {
  const [view, setView] = useState<DrawerView>('roster');
  const [members, setMembers] = useState<TripMembership[]>([]);
  const [sentInvites, setSentInvites] = useState<ConnectionInvite[]>([]);
  const [connections, setConnections] = useState<ConnectionRow[]>([]);
  const [displayNames, setDisplayNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState<ShareRole>('viewer');
  const [channel, setChannel] = useState<InviteChannel>('qr');
  const [selectedSubs, setSelectedSubs] = useState<string[]>([]);
  const [connectionSearch, setConnectionSearch] = useState('');
  const [inviting, setInviting] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [actingSub, setActingSub] = useState<string | null>(null);

  const isCommunityEnabled = (): boolean =>
    loadLocalUserPreferences().appUserSettings.communityFeaturesEnabled;

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const [rows, invites, connRows] = await Promise.all([
        listMembers(tripId),
        listPendingSentInvitesForTrip(tripId),
        listConnections().catch(() => [] as ConnectionRow[]),
      ]);
      const visible = rows.filter((m) => m.status !== 'revoked');
      setMembers(visible);
      setSentInvites(invites);
      setConnections(
        connRows.map((r) => ({
          connectedUserId: r.connectedUserId,
          displayName: r.displayName ?? r.username ?? null,
          username: r.username ?? null,
        }))
      );
      const names = await resolveDisplayNames(
        visible.map((m) => m.memberSub),
        connRows
      );
      setDisplayNames(names);
    } catch (e) {
      message.error(e instanceof Error ? e.message : t.retry);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    if (!open) return;
    setView('roster');
    setChannel('qr');
    setRole('viewer');
    setSelectedSubs([]);
    setConnectionSearch('');
    setQrDataUrl(null);
    setQrError(null);
    void reload();
  }, [open, reload]);

  const regenerateQr = useCallback(async (): Promise<void> => {
    setQrLoading(true);
    setQrError(null);
    try {
      if (!isCommunityEnabled()) {
        setQrDataUrl(null);
        return;
      }
      const end = String(tripEndDate ?? '').trim();
      if (!end) {
        setQrDataUrl(null);
        setQrError(t.qrEndDateRequired);
        return;
      }
      const ctx = await createQrCodeToken({
        tripId,
        tripEndDate: end,
        role,
      });
      const url = qrInvitationUrl(ctx.token);
      const dataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: 'H',
        width: 200,
        margin: 1,
      });
      setQrDataUrl(dataUrl);
    } catch (e) {
      captureSanitizedException('trip_share_qr', e);
      setQrDataUrl(null);
      if (e instanceof QrCodeApiError) {
        setQrError(e.message.includes('end date') ? t.qrEndDateRequired : t.qrGenerateFailed);
      } else {
        setQrError(t.qrGenerateFailed);
      }
    } finally {
      setQrLoading(false);
    }
  }, [tripId, tripEndDate, role]);

  const runAfterCommunityGate = useCallback(
    (action: () => void): void => {
      if (isCommunityEnabled()) {
        action();
        return;
      }
      Modal.confirm({
        title: t.enableCommunityTitle,
        content: t.enableCommunityMessage,
        okText: t.enableCommunityConfirm,
        cancelText: t.cancel,
        onOk: () => {
          patchAppUserSettings({ communityFeaturesEnabled: true });
          action();
        },
      });
    },
    []
  );

  const ensureCommunityEnabled = useCallback((): Promise<boolean> => {
    if (isCommunityEnabled()) return Promise.resolve(true);
    return new Promise((resolve) => {
      Modal.confirm({
        title: t.enableCommunityTitle,
        content: t.enableCommunityMessage,
        okText: t.enableCommunityConfirm,
        cancelText: t.cancel,
        onOk: () => {
          patchAppUserSettings({ communityFeaturesEnabled: true });
          resolve(true);
        },
        onCancel: () => resolve(false),
      });
    });
  }, []);

  const openInvite = useCallback((): void => {
    setView('invite');
    setChannel('qr');
    setSelectedSubs([]);
    // useEffect regenerates QR when community is already on; gate only when off.
    if (!isCommunityEnabled()) {
      runAfterCommunityGate(() => {
        void regenerateQr();
      });
    }
  }, [regenerateQr, runAfterCommunityGate]);

  useEffect(() => {
    if (!open || view !== 'invite' || channel !== 'qr') return;
    if (!isCommunityEnabled()) return;
    void regenerateQr();
  }, [open, view, channel, role, regenerateQr]);

  const memberSubs = useMemo(
    () => new Set(members.map((m) => m.memberSub)),
    [members]
  );

  const activeMembers = useMemo(
    () => members.filter((m) => m.status === 'active'),
    [members]
  );

  const pendingMemberships = useMemo(
    () => members.filter((m) => m.status === 'pending'),
    [members]
  );

  const hasSentInvites = pendingMemberships.length > 0 || sentInvites.length > 0;

  const filteredConnections = useMemo(() => {
    const q = connectionSearch.trim().toLowerCase();
    return connections
      .filter((c) => !memberSubs.has(c.connectedUserId))
      .filter((c) => {
        if (!q) return true;
        const label = (
          c.displayName ||
          c.username ||
          c.connectedUserId
        ).toLowerCase();
        return label.includes(q);
      });
  }, [connections, memberSubs, connectionSearch]);

  const onRoleChange = async (
    member: TripMembership,
    next: ShareRole
  ): Promise<void> => {
    if (member.role === 'owner') return;
    setActingSub(member.memberSub);
    try {
      await patchMemberRole(tripId, member.memberSub, next);
      await reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : t.retry);
    } finally {
      setActingSub(null);
    }
  };

  const onRevoke = async (member: TripMembership): Promise<void> => {
    if (member.role === 'owner') return;
    setActingSub(member.memberSub);
    try {
      await revokeMember(tripId, member.memberSub);
      await reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : t.retry);
    } finally {
      setActingSub(null);
    }
  };

  const toggleConnection = (sub: string): void => {
    setSelectedSubs((prev) =>
      prev.includes(sub) ? prev.filter((s) => s !== sub) : [...prev, sub]
    );
  };

  const inviteConnections = async (): Promise<void> => {
    if (selectedSubs.length === 0) return;
    runAfterCommunityGate(() => {
      void (async () => {
        setInviting(true);
        let successCount = 0;
        let lastError: unknown;
        try {
          for (const memberSub of selectedSubs) {
            try {
              await addMember(tripId, memberSub, role);
              successCount += 1;
            } catch (e) {
              lastError = e;
              if (e instanceof TripShareApiError && e.status === 409) {
                message.error(t.memberCap);
                break;
              }
            }
          }
          if (successCount > 0) {
            if (isPostHogConfigured) {
              posthog.capture('trip_share_invite_sent', {
                role,
                member_count_after: members.length + successCount,
                channel: 'connection',
              });
            }
            message.success(
              successCount === 1 ? t.inviteSentOne : t.inviteSentMany(successCount)
            );
            if (lastError) {
              captureSanitizedException('trip_share_invite', lastError);
              message.error(
                lastError instanceof Error ? lastError.message : t.retry
              );
            }
            setSelectedSubs([]);
            await reload();
            if (!lastError) setView('roster');
          } else if (lastError) {
            captureSanitizedException('trip_share_invite', lastError);
            message.error(
              lastError instanceof Error ? lastError.message : t.retry
            );
          }
        } finally {
          setInviting(false);
        }
      })();
    });
  };

  const memberLabel = (sub: string): string => displayNameForSub(displayNames, sub);

  const renderMemberRow = (m: TripMembership) => {
    const isOwner = m.role === 'owner';
    const isEditor = m.role === 'editor' || isOwner;
    const isPending = m.status === 'pending';
    return (
      <List.Item
        key={m.id || m.memberSub}
        actions={[
          <Checkbox
            key="editor"
            checked={isEditor}
            disabled={isOwner || actingSub === m.memberSub}
            aria-label={t.editor}
            onChange={(e) =>
              void onRoleChange(m, e.target.checked ? 'editor' : 'viewer')
            }
          >
            {t.editor}
          </Checkbox>,
          isPending ? (
            <Button
              key="resend"
              type="link"
              size="small"
              onClick={openInvite}
              aria-label={t.resendInvite}
            >
              {t.resendInvite}
            </Button>
          ) : null,
          !isOwner ? (
            <Button
              key="rm"
              danger
              size="small"
              loading={actingSub === m.memberSub}
              onClick={() => void onRevoke(m)}
            >
              {t.remove}
            </Button>
          ) : null,
        ].filter(Boolean)}
      >
        <List.Item.Meta
          title={memberLabel(m.memberSub)}
          description={`${isPending ? t.pending : t.active} · ${m.role}`}
        />
      </List.Item>
    );
  };

  const rosterBody = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {tripName ? (
        <Typography.Title level={4} style={{ margin: 0 }}>
          {tripName}
        </Typography.Title>
      ) : null}
      <Typography.Text type="secondary">{t.memberCapHint}</Typography.Text>

      {loading ? (
        <Spin tip={t.loading} />
      ) : (
        <>
          <List
            header={t.activeMembers}
            locale={{ emptyText: t.emptyRoster }}
            dataSource={activeMembers}
            renderItem={renderMemberRow}
          />
          <List
            header={t.sentInvites}
            locale={{ emptyText: t.sentInvitesEmpty }}
            dataSource={
              hasSentInvites
                ? ([
                    ...pendingMemberships.map((m) => ({ kind: 'membership' as const, m })),
                    ...sentInvites.map((inv) => ({ kind: 'invite' as const, inv })),
                  ] as Array<
                    | { kind: 'membership'; m: TripMembership }
                    | { kind: 'invite'; inv: ConnectionInvite }
                  >)
                : []
            }
            renderItem={(item) => {
              if (item.kind === 'membership') {
                return renderMemberRow(item.m);
              }
              const inv = item.inv;
              const roleLabel =
                inv.role === 'editor'
                  ? t.editor
                  : inv.role === 'viewer'
                    ? t.viewer
                    : inv.role || t.viewer;
              return (
                <List.Item
                  key={inv.id}
                  actions={[
                    <Button
                      key="resend"
                      type="link"
                      size="small"
                      onClick={openInvite}
                      aria-label={t.resendInvite}
                    >
                      {t.resendInvite}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={connectionInvitePrimaryLabel(inv.inviteeUserId) || inv.id}
                    description={`${t.pending} · ${roleLabel}`}
                  />
                </List.Item>
              );
            }}
          />
        </>
      )}

      <Button type="primary" block onClick={openInvite}>
        {t.invite}
      </Button>
    </Space>
  );

  const inviteBody = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Button type="link" onClick={() => setView('roster')} style={{ padding: 0 }}>
        {t.backToRoster}
      </Button>

      {(channel === 'qr' || channel === 'connection') && (
        <Radio.Group value={role} onChange={(e) => setRole(e.target.value)}>
          <Radio.Button value="viewer">{t.viewer}</Radio.Button>
          <Radio.Button value="editor">{t.editor}</Radio.Button>
        </Radio.Group>
      )}

      <Radio.Group
        value={channel}
        optionType="button"
        buttonStyle="solid"
        aria-label={t.channelPicker}
        onChange={(e) => {
          const next = e.target.value as InviteChannel;
          setChannel(next);
          if (next === 'qr' && !isCommunityEnabled()) {
            runAfterCommunityGate(() => {
              void regenerateQr();
            });
          }
        }}
        options={[
          { value: 'qr', label: t.channelQr },
          { value: 'connection', label: t.channelConnection },
          { value: 'email', label: t.channelEmail },
          { value: 'sms', label: t.channelSms },
        ]}
      />

      {channel === 'qr' ? (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {t.channelsHint}
          </Typography.Paragraph>
          {qrLoading ? (
            <Spin tip={t.loading} />
          ) : qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt={t.channelQr}
              width={200}
              height={200}
              style={{ display: 'block', margin: '0 auto' }}
            />
          ) : qrError ? (
            <Typography.Text type="danger">{qrError}</Typography.Text>
          ) : !isCommunityEnabled() ? (
            <Typography.Text type="secondary">{t.enableCommunityMessage}</Typography.Text>
          ) : null}
          <Button
            onClick={() =>
              runAfterCommunityGate(() => {
                void regenerateQr();
              })
            }
          >
            {t.refreshQr}
          </Button>
        </Space>
      ) : null}

      {channel === 'connection' ? (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Input
            allowClear
            placeholder={t.searchConnections}
            value={connectionSearch}
            onChange={(e) => setConnectionSearch(e.target.value)}
            aria-label={t.searchConnections}
          />
          <Typography.Text type="secondary">{t.selectConnections}</Typography.Text>
          {filteredConnections.length === 0 ? (
            <Typography.Text type="secondary">{t.noConnections}</Typography.Text>
          ) : (
            <List
              size="small"
              dataSource={filteredConnections}
              renderItem={(c) => (
                <List.Item>
                  <Checkbox
                    checked={selectedSubs.includes(c.connectedUserId)}
                    onChange={() => toggleConnection(c.connectedUserId)}
                  >
                    {c.displayName || c.username || c.connectedUserId}
                  </Checkbox>
                </List.Item>
              )}
            />
          )}
          <Button
            type="primary"
            loading={inviting}
            disabled={selectedSubs.length === 0}
            onClick={() => void inviteConnections()}
          >
            {t.sendInvite}
          </Button>
        </Space>
      ) : null}

      {channel === 'email' || channel === 'sms' ? (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Radio.Group value={role} onChange={(e) => setRole(e.target.value)}>
            <Radio.Button value="viewer">{t.viewer}</Radio.Button>
            <Radio.Button value="editor">{t.editor}</Radio.Button>
          </Radio.Group>
          <ConnectionInviteForm
            tripId={tripId}
            role={role}
            channel={channel}
            hideHeading
            onBeforeSend={ensureCommunityEnabled}
            onSent={() => {
              void reload();
            }}
          />
        </Space>
      ) : null}
    </Space>
  );

  return (
    <Drawer
      title={view === 'roster' ? t.peopleWithAccess : t.invite}
      open={open}
      onClose={onClose}
      width={440}
      destroyOnClose
    >
      {view === 'roster' ? rosterBody : inviteBody}
    </Drawer>
  );
};

export default TripShareRoster;
